import StyledSelectDropdown from "../StyledSelectDropdown"
import { FormField, FORM_LABEL_CLASS, SectionHeader } from "./CreateJobFields"

type AdditionalRequirementsSectionProps = {
    isEditMode: boolean
    genders: Array<{ value: string; label: string }>
    gendersStatus: string
    genderValue: string | null
    visaStatuses: Array<{ value: string; label: string }>
    visaStatusesStatus: string
    visaStatusValue: string | null
    drivingLicenses: Array<{ value: string; label: string }>
    drivingLicensesStatus: string
    drivingLicenseValue: string | null
    tasks: string
    isGeneratingDescription?: boolean
    isDescriptionReadOnly?: boolean
    onGenderChange: (value: string) => void
    onVisaStatusChange: (value: string) => void
    onDrivingLicenseChange: (value: string) => void
    onTasksChange: (value: string) => void
    onDescriptionFocus?: () => void
}

export const AdditionalRequirementsSection = ({
    isEditMode,
    genders,
    gendersStatus,
    genderValue,
    visaStatuses,
    visaStatusesStatus,
    visaStatusValue,
    drivingLicenses,
    drivingLicensesStatus,
    drivingLicenseValue,
    tasks,
    isGeneratingDescription = false,
    isDescriptionReadOnly = false,
    onGenderChange,
    onVisaStatusChange,
    onDrivingLicenseChange,
    onTasksChange,
    onDescriptionFocus,
}: AdditionalRequirementsSectionProps) => (
    <section className="mb-10">
        <SectionHeader title="Additional requirements" />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
            <div>
                <label className={`${FORM_LABEL_CLASS} block mb-4`}>Gender</label>
                <div className="flex items-center gap-6">
                    {gendersStatus === "loading" && (
                        <span className="text-sm text-gray-500">Loading...</span>
                    )}
                    {gendersStatus !== "loading" &&
                        genders.map((gender) => (
                            <label
                                key={gender.value}
                                className={`flex items-center gap-1 ${isEditMode ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
                                onClick={() => !isEditMode && onGenderChange(gender.value)}
                            >
                                <div
                                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${genderValue === gender.value ? "border-[#ADA7A7]" : "border-gray-300"}`}
                                >
                                    {genderValue === gender.value && <div className="w-3 h-3 bg-[#ADA7A7] rounded-full" />}
                                </div>
                                <span
                                    className={`text-small transition-colors ${genderValue === gender.value ? "text-gray-900 font-medium" : "text-gray-500"}`}
                                >
                                    {gender.label}
                                </span>
                            </label>
                        ))}
                </div>
            </div>

            <FormField label="Valid visa" required>
                <StyledSelectDropdown
                    options={visaStatuses.map((visaStatus) => ({ id: visaStatus.value, name: visaStatus.label }))}
                    value={visaStatusValue || ""}
                    onChange={onVisaStatusChange}
                    placeholder="Select visa status"
                    disabled={visaStatusesStatus === "loading" || isEditMode}
                />
            </FormField>

            <FormField label="Driving license" required>
                <StyledSelectDropdown
                    options={drivingLicenses.map((drivingLicense) => ({ id: drivingLicense.value, name: drivingLicense.label }))}
                    value={drivingLicenseValue || ""}
                    onChange={onDrivingLicenseChange}
                    placeholder="Select driving license"
                    disabled={drivingLicensesStatus === "loading" || isEditMode}
                />
            </FormField>
        </div>
        <div className="md:col-span-2">
            <FormField label="Description of the tasks" required>
                <textarea
                    className={`w-full bg-[#F9F9F9] p-4 rounded-md border-none text-small min-h-[100px] resize-none placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-lime-400 ${tasks ? "text-gray-800" : "text-gray-400"}`}
                    placeholder={isGeneratingDescription ? "Generating job description..." : "Click here to generate job description..."}
                    value={tasks}
                    readOnly={isDescriptionReadOnly || isGeneratingDescription}
                    onFocus={onDescriptionFocus}
                    onChange={(event) => onTasksChange(event.target.value)}
                />
            </FormField>
        </div>
    </section>
)
